import { TimeOffHistoryGraph } from "@/pages/time-off/components/TimeOffHistoryGraph.tsx";
import { TimeOffHistoryLoadingSkeleton } from "@/pages/time-off/components/TimeOffHistoryLoadingSkeleton.tsx";
import { useEmployeeTimeOffHistory } from "@/queries/employees/use-employees.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiEmployeeTimeOffCategory,
  ApiEmployeeTimeOffHistoryFilter,
} from "@/types/time-off/time-off.types.ts";
import { useModals, Modal, ModalProps } from "@skailer-com/uikit";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEffectOnce } from "react-use";

export interface TimeOffHistoryDetailsModalProps extends ModalProps {
  employeeId: number;
  timeOffCategory: ApiEmployeeTimeOffCategory;
}

export function TimeOffHistoryDetailsModal({
  timeOffCategory,
  employeeId,
  ...props
}: TimeOffHistoryDetailsModalProps) {
  const { close } = useModals();
  const { id } = { ...props };

  const [historyFilter, setHistoryFilter] = useState<ApiEmployeeTimeOffHistoryFilter>({
    year: new Date().getFullYear(),
    time_off_category_id: timeOffCategory.id,
  });
  const { data: history, isPending } = useEmployeeTimeOffHistory(employeeId, historyFilter);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.employees.timeOffHistoryViewed, {
      [analyticProperties.id]: employeeId,
    });
  });

  const showPeriod = useCallback(
    (next: boolean) => {
      setHistoryFilter({ ...historyFilter, year: historyFilter.year + (next ? 1 : -1) });
    },
    [historyFilter]
  );

  const navigate = useNavigate();

  return (
    <Modal {...props} size={"lg"} withCloser={true} title={timeOffCategory?.title ?? ""}>
      <>
        {isPending && <TimeOffHistoryLoadingSkeleton />}
        {!isPending && (
          <TimeOffHistoryGraph
            showLogs={() => {
              close(id);
              navigate(`/employees/${employeeId}/time-off-history/${timeOffCategory.id}`);
            }}
            showPeriod={showPeriod}
            timeOffCategory={timeOffCategory}
            employeeId={employeeId}
            history={history}
            {...props}
          />
        )}
      </>
    </Modal>
  );
}
